import type { ReactNode } from 'react';
import { useFieldError } from '../a11y/useFieldError';
import styles from './FormField.module.css';

export interface FormFieldRenderArgs {
  /** Spread onto the control: id, aria-invalid, aria-describedby. */
  controlProps: {
    id: string;
    'aria-invalid': boolean;
    'aria-describedby'?: string;
  };
}

export interface FormFieldProps {
  label: string;
  /** Explicit id; auto-generated when omitted. */
  id?: string;
  helpText?: string;
  /** Inline node rendered beside the label (e.g. a HelpTip) — kept OUTSIDE the
   * <label> so it never breaks the label's accessible name. */
  labelHelp?: ReactNode;
  /** Field-level error message (client validation or per-field 422). */
  error?: string;
  /** Render-prop receiving the wired control props. */
  children: (args: FormFieldRenderArgs) => ReactNode;
}

/**
 * Labels a form control and wires its ARIA relationships via `useFieldError`:
 * `aria-invalid` and `aria-describedby` (helper `#<id>-help`, error `#<id>-err`).
 */
export function FormField({ label, id, helpText, labelHelp, error, children }: FormFieldProps) {
  const hasError = Boolean(error);
  const hasHelp = Boolean(helpText);
  const { id: fieldId, helpId, errorId, controlProps } = useFieldError({
    ...(id ? { id } : {}),
    hasError,
    hasHelp,
  });

  return (
    <div className={styles.field}>
      {labelHelp ? (
        <span className={styles.labelRow}>
          <label className={styles.label} htmlFor={fieldId}>
            {label}
          </label>
          {labelHelp}
        </span>
      ) : (
        <label className={styles.label} htmlFor={fieldId}>
          {label}
        </label>
      )}
      {children({ controlProps })}
      {hasHelp ? (
        <span id={helpId} className={styles.help}>
          {helpText}
        </span>
      ) : null}
      {hasError ? (
        <span id={errorId} className={styles.error}>
          {error}
        </span>
      ) : null}
    </div>
  );
}
